import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Background,
  BackgroundVariant,
  Controls,
  Panel,
  ReactFlow,
  useReactFlow,
  type Edge,
  type IsValidConnection,
  type NodeTypes,
} from "@xyflow/react";
import { ContextMenu, type MenuTarget } from "~/components/ContextMenu";
import { GraphNode } from "~/components/GraphNode";
import { Button } from "~/components/ui/Button";
import type { SpiroNode } from "~/lib/graph";
import { useGraphClock } from "~/lib/useGraphClock";
import { NODE_DEFINITIONS, getDefinition } from "~/nodes/registry";
import { useGraphStore } from "~/store/graphStore";

/** Every registered type renders through the same definition-driven component. */
const nodeTypes: NodeTypes = Object.fromEntries(
  NODE_DEFINITIONS.map((definition) => [definition.type, GraphNode]),
);

interface GraphEditorProps {
  onSave: () => void;
  onLoad: () => void;
  onExportPoints: () => void;
}

export function GraphEditor({
  onSave,
  onLoad,
  onExportPoints,
}: GraphEditorProps) {
  const nodes = useGraphStore((state) => state.nodes);
  const edges = useGraphStore((state) => state.edges);
  const onNodesChange = useGraphStore((state) => state.onNodesChange);
  const onEdgesChange = useGraphStore((state) => state.onEdgesChange);
  const onConnect = useGraphStore((state) => state.onConnect);
  const clear = useGraphStore((state) => state.clear);

  const generation = useGraphStore((state) => state.generation);

  const { screenToFlowPosition, fitView } = useReactFlow<SpiroNode, Edge>();
  const [menu, setMenu] = useState<MenuTarget | null>(null);

  useGraphClock();

  // Sketches arrive from Firestore after mount, so the initial `fitView` has
  // nothing to frame. Re-fit whenever the graph is replaced wholesale.
  useEffect(() => {
    if (generation === 0) return;

    // Wait for xyflow to measure the freshly mounted nodes.
    const timer = setTimeout(
      () => void fitView({ padding: 0.15, duration: 300 }),
      50,
    );
    return () => clearTimeout(timer);
  }, [generation, fitView]);

  /** Only ports of the same socket kind may be joined — rete enforced this too. */
  const isValidConnection = useCallback<IsValidConnection>(
    (connection) => {
      if (connection.source === connection.target) return false;

      const source = nodes.find((node) => node.id === connection.source);
      const target = nodes.find((node) => node.id === connection.target);
      if (!source || !target) return false;

      const from = getDefinition(source.type)?.outputs.find(
        (o) => o.key === connection.sourceHandle,
      );
      const to = getDefinition(target.type)?.inputs.find(
        (i) => i.key === connection.targetHandle,
      );

      return (
        from !== undefined && to !== undefined && from.socket === to.socket
      );
    },
    [nodes],
  );

  const defaultEdgeOptions = useMemo(
    () => ({ type: "smoothstep" as const, animated: false }),
    [],
  );

  return (
    <ReactFlow<SpiroNode, Edge>
      nodes={nodes}
      edges={edges}
      nodeTypes={nodeTypes}
      onNodesChange={onNodesChange}
      onEdgesChange={onEdgesChange}
      onConnect={onConnect}
      isValidConnection={isValidConnection}
      defaultEdgeOptions={defaultEdgeOptions}
      onPaneContextMenu={(event) => {
        event.preventDefault();
        setMenu({
          kind: "pane",
          screen: { x: event.clientX, y: event.clientY },
          flow: screenToFlowPosition({ x: event.clientX, y: event.clientY }),
        });
      }}
      onNodeContextMenu={(event, node) => {
        event.preventDefault();
        setMenu({
          kind: "node",
          screen: { x: event.clientX, y: event.clientY },
          nodeId: node.id,
        });
      }}
      onPaneClick={() => setMenu(null)}
      deleteKeyCode={["Backspace", "Delete"]}
      minZoom={0.1}
      proOptions={{ hideAttribution: false }}
      fitView
    >
      <Background
        variant={BackgroundVariant.Dots}
        gap={20}
        size={1}
        color="#3f3f46"
      />
      <Controls showInteractive={false} />

      <Panel position="top-left" className="flex flex-wrap gap-2">
        <Button onClick={onSave}>Save</Button>
        <Button onClick={onLoad}>Load</Button>
        <Button variant="secondary" onClick={clear}>
          Clear
        </Button>
        <Button variant="ghost" onClick={onExportPoints}>
          Get Points (json)
        </Button>
      </Panel>

      {menu ? (
        <ContextMenu target={menu} onClose={() => setMenu(null)} />
      ) : null}
    </ReactFlow>
  );
}
